import { extractGlazingSlots, type GlazingSlot } from './glazingSlots';
import { extractHardwareGuides, type HardwareGuides } from './hardwareGuides';

export interface PreparedExternalSlabArtwork {
  viewBox: string;
  referenceWidth: number;
  referenceHeight: number;
  markup: string;
  glazingSlots: GlazingSlot[];
  hardwareGuides: HardwareGuides;
}

export const SLAB_ARTWORK_REFERENCE_WIDTH = 780;
export const SLAB_ARTWORK_REFERENCE_HEIGHT = 1975;

const GUIDE_SELECTOR = '#guides, [id="guides"]';
const GLAZING_GUIDE_GROUP_SELECTOR = '#glazing-guides, [id="glazing-guides"]';
const HARDWARE_GUIDE_GROUP_SELECTOR = '#hardware-guides, [id="hardware-guides"]';

export function parseViewBoxSize(viewBox: string): { width: number; height: number } {
  const parts = viewBox.trim().split(/[\s,]+/).map(Number);
  if (parts.length === 4 && parts.every((value) => Number.isFinite(value))) {
    return { width: parts[2], height: parts[3] };
  }
  return { width: SLAB_ARTWORK_REFERENCE_WIDTH, height: SLAB_ARTWORK_REFERENCE_HEIGHT };
}

function parseViewBox(root: Element): string {
  const authored = root.getAttribute('viewBox')?.trim();
  if (authored) return authored;
  const width = Number(root.getAttribute('width'));
  const height = Number(root.getAttribute('height'));
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return `0 0 ${width} ${height}`;
  }
  return `0 0 ${SLAB_ARTWORK_REFERENCE_WIDTH} ${SLAB_ARTWORK_REFERENCE_HEIGHT}`;
}

function innerMarkup(root: Element): string {
  return Array.from(root.childNodes)
    .filter((node) => {
      if (node.nodeType !== 1) return false;
      const name = (node as Element).tagName.toLowerCase();
      return name !== 'namedview' && !name.endsWith(':namedview');
    })
    .map((node) => (node as Element).outerHTML)
    .join('');
}

/**
 * Keep authored panel/bevel markup. Strip production guides, glazing-guide
 * metadata, and hardware-guide metadata so they never paint. Slot and hardware
 * placement are read from those groups before they are removed.
 */
export function prepareExternalSlabArtwork(svgText: string): PreparedExternalSlabArtwork | null {
  const document = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  if (document.querySelector('parsererror')) return null;
  const root = document.documentElement;
  if (root.tagName.toLowerCase() !== 'svg') return null;

  root.querySelectorAll(GUIDE_SELECTOR).forEach((node) => node.remove());
  const glazingSlots = extractGlazingSlots(root);
  const hardwareGuides = extractHardwareGuides(root);
  root.querySelectorAll(GLAZING_GUIDE_GROUP_SELECTOR).forEach((node) => node.remove());
  root.querySelectorAll(HARDWARE_GUIDE_GROUP_SELECTOR).forEach((node) => node.remove());
  const markup = innerMarkup(root);
  if (!markup.trim()) return null;

  const viewBox = parseViewBox(root);
  const size = parseViewBoxSize(viewBox);
  return {
    viewBox,
    referenceWidth: size.width,
    referenceHeight: size.height,
    markup,
    glazingSlots,
    hardwareGuides,
  };
}
